import type { Money } from '../api/contract';

/** 'ES' → 🇪🇸 (regional-indicator pair). Empty for anything that isn't two letters. */
export function flagEmoji(countryCode: string | null | undefined): string {
  if (!countryCode || !/^[a-z]{2}$/i.test(countryCode)) return '';
  const base = 0x1f1e6 - 'A'.charCodeAt(0);
  return String.fromCodePoint(...[...countryCode.toUpperCase()].map((c) => base + c.charCodeAt(0)));
}

/**
 * The language dates, numbers and money are written in: the screens' language (i18n sets it
 * when a locale becomes active). English keeps the prototype's British forms.
 */
let current = 'en';
export function setFormatLocale(locale: string) {
  current = locale;
}

/**
 * The Intl locale for the screens' language: Western digits and the Gregorian calendar in
 * every locale for now (the POA's decision: ar, fa, bn… would otherwise use their own digits,
 * fa and th their own calendars).
 */
export function intlLocale(locale = current): string {
  return locale === 'en' ? 'en-GB' : `${locale}-u-ca-gregory-nu-latn`;
}

/** 1234 → '1,234' in the screens' language (Western digits). */
export function formatNumber(n: number): string {
  return new Intl.NumberFormat(intlLocale()).format(n);
}

/** { amount: 250, currency: 'EUR' } → '€250'. Whole major units (design rule 10). */
export function formatMoney(m: Money, locale = intlLocale()): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: m.currency,
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(m.amount);
}

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Local calendar date 'YYYY-MM-DD' → '8 Sep 2026' (the prototype's fmtDate). No time zones involved. */
export function formatDate(iso: string | null | undefined): string {
  const m = iso ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso) : null;
  if (!m) return '';
  if (current !== 'en') return intlDate(m, { day: 'numeric', month: 'short', year: 'numeric' });
  return `${Number(m[3])} ${SHORT_MONTHS[Number(m[2]) - 1]} ${m[1]}`;
}

/** Another language's date, as a calendar date (no time zone moves it). */
function intlDate(m: RegExpExecArray, opts: Intl.DateTimeFormatOptions): string {
  const at = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3] ?? 1));
  return new Intl.DateTimeFormat(intlLocale(), { ...opts, timeZone: 'UTC' }).format(at);
}

const LONG_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** 'YYYY-MM' → 'October 2017' (when a defunct airline stopped flying). */
export function formatMonth(ym: string | null | undefined): string {
  const m = ym ? /^(\d{4})-(\d{2})$/.exec(ym) : null;
  if (m && current !== 'en') return intlDate(m, { month: 'long', year: 'numeric' });
  const name = m ? LONG_MONTHS[Number(m[2]) - 1] : undefined;
  return m && name ? `${name} ${m[1]}` : '';
}

/** 'anna@example.com' → 'a•••@example.com' (S14-expired). */
export function maskEmail(email: string): string {
  const [u, d] = email.split('@');
  return u && d ? `${u[0]}•••@${d}` : email;
}

/** ['Ben'] → 'Ben'; ['Ben', 'Cara', 'Mia'] → 'Ben, Cara and Mia' (in the screens' language). */
export function andList(xs: string[]): string {
  if (current !== 'en' && xs.length > 1)
    return new Intl.ListFormat(intlLocale(), { type: 'conjunction' }).format(xs);
  return xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;
}

const TIME_ZONE = /^(UTC|[A-Za-z]+(\/[A-Za-z0-9_+-]+){1,2})$/;

/**
 * The browser's IANA time zone ('Europe/Madrid'), sent with a signature and the assignment
 * previews so the document prints "Date signed" where the signer is (Q47, w5-07). Undefined
 * when the browser gives none, or something that isn't a zone name (contract `TimeZone`).
 */
export function signerTimeZone(): string | undefined {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof zone === 'string' && zone.length <= 64 && TIME_ZONE.test(zone) ? zone : undefined;
  } catch {
    return undefined;
  }
}
